import axios from 'axios';
import { toast } from 'react-toastify';

// ─── Base URL ────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

// ─── Create Axios Instance ──────────────────────────────────
const axiosAgent = axios.create({
    baseURL: BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
        'Content-Type': 'application/json',
        Accept: '*/*'
    }
});

// ─── Request Interceptor ────────────────────────────────────
// Automatically attach the latest token to every request
axiosAgent.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Don't override Content-Type for FormData (let browser set multipart boundary)
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// ─── Response Interceptor ───────────────────────────────────
// Centralized error handling for all API responses
axiosAgent.interceptors.response.use(
    (response) => {
        // Successful response — pass through
        return response;
    },
    (error) => {
        // No response from server (network error / timeout)
        if (!error.response) {
            toast.error(
                'Network error. Please check your internet connection.'
            );
            console.error('Network Error:', error.message);
            return Promise.reject(error);
        }

        const { status, data } = error.response;
        const message = data?.message || error.message;

        switch (status) {
            case 400:
                toast.error(message || 'Bad request. Please check your input.');
                break;

            case 401:
                toast.error('Unauthorised access');
                // Clear auth data and redirect to login
                localStorage.removeItem('token');
                // Small delay so the toast is visible before redirect
                setTimeout(() => {
                    window.location.href = '/';
                }, 1500);
                // Return a never-resolving promise so the calling component
                // doesn't crash or throw an unhandled rejection while we redirect
                return new Promise(() => {});

            case 403:
                toast.error('Access denied. You do not have permission.');
                break;

            case 404:
                toast.error(message || 'Requested resource not found.');
                break;

            case 409:
                toast.error(
                    message || 'Conflict. The resource already exists.'
                );
                break;

            case 422:
                toast.error(
                    message || 'Validation error. Please check your data.'
                );
                break;

            case 500:
                toast.error('Internal server error. Please try again later.');
                break;

            default:
                toast.error(
                    message || `Something went wrong (Error ${status})`
                );
                break;
        }

        console.error(`API Error [${status}]:`, message);
        return Promise.reject(error);
    }
);

export default axiosAgent;
